/**
 * Schreibt das Dashboard-Layout auf den Server — nacheinander, nie parallel.
 *
 * Codex-Review 26.09.2026, zwei Befunde am ersten Wurf:
 *   1. Dauerte ein Speichern laenger als die Verzoegerung und wurde weiter
 *      umgebaut, liefen zwei Anfragen parallel. Kam die aeltere zuletzt an,
 *      stand auf dem Server der alte Stand — und die Anzeige sagte
 *      „Gespeichert". patchProfileSettings kennt keinen Zeitstempel.
 *   2. Scheiterte das Speichern und wurde das Dashboard danach verlassen, war
 *      der Stand vergessen: nie erneut geschickt, kein Fehler mehr sichtbar.
 *
 * Deshalb lebt der Speicherzustand hier auf Modulebene je Nutzer (ueberlebt
 * das Aus- und Einhaengen des Dashboards), es laeuft hoechstens EINE Anfrage,
 * und waehrenddessen wird nur der juengste Stand vorgemerkt. Ein
 * gescheiterter Stand bleibt vorgemerkt, bis ein Erneut-Versuch oder eine
 * weitere Aenderung ihn (oder einen juengeren) durchbringt.
 *
 * Ohne React und mit austauschbarer Schreibfunktion — damit testbar.
 */
import type { DashboardLayout } from './dashboardLayout';

export type LayoutSaveState = 'idle' | 'saving' | 'saved' | 'error';

export type LayoutWriter = (userId: string, layout: DashboardLayout) => Promise<unknown>;

interface UserSaveState {
    inflight: boolean;
    /** Noch nicht bestaetigter, juengster Stand (auch nach einem Fehlschlag). */
    queued: DashboardLayout | null;
    status: LayoutSaveState;
    /** updatedAt des zuletzt vom Server bestaetigten Stands (ms), sonst null. */
    confirmedMs: number | null;
    listeners: Set<() => void>;
}

const ms = (iso: string | null | undefined): number => {
    const t = iso ? Date.parse(iso) : NaN;
    return Number.isFinite(t) ? t : 0;
};

export interface LayoutSaver {
    /** Stand zum Speichern vormerken und die Schlange anstossen. */
    request: (userId: string, layout: DashboardLayout) => void;
    /** Vorgemerkten (gescheiterten) Stand erneut schicken; ohne Vormerkung den genannten. */
    retry: (userId: string, fallback?: DashboardLayout) => void;
    status: (userId: string) => LayoutSaveState;
    subscribe: (userId: string, fn: () => void) => () => void;
    /**
     * Muss dieser Stand noch auf den Server? Ja, wenn er juenger ist als der
     * Serverstand UND als alles, was der Server in dieser Sitzung bestaetigt
     * hat, und nicht schon vorgemerkt ist. Die Vorlage ohne eigenen Stand
     * (Zeitstempel = Epoche) muss nie hoch.
     */
    needsPush: (userId: string, layout: DashboardLayout, serverUpdatedAt: string | null) => boolean;
    /** Wartet, bis die Schlange leer ist oder gescheitert (fuer Tests). */
    idle: (userId: string) => Promise<void>;
}

export const createLayoutSaver = (write: LayoutWriter): LayoutSaver => {
    const users = new Map<string, UserSaveState>();
    const waiters = new Map<string, Array<() => void>>();

    const stateOf = (userId: string): UserSaveState => {
        let s = users.get(userId);
        if (!s) {
            s = { inflight: false, queued: null, status: 'idle', confirmedMs: null, listeners: new Set() };
            users.set(userId, s);
        }
        return s;
    };

    const setStatus = (s: UserSaveState, status: LayoutSaveState) => {
        if (s.status === status) return;
        s.status = status;
        s.listeners.forEach(fn => fn());
    };

    const settle = (userId: string) => {
        const list = waiters.get(userId);
        waiters.delete(userId);
        list?.forEach(fn => fn());
    };

    const pump = async (userId: string) => {
        const s = stateOf(userId);
        if (s.inflight) return;
        s.inflight = true;
        try {
            while (s.queued) {
                const next = s.queued;
                s.queued = null;
                setStatus(s, 'saving');
                try {
                    await write(userId, next);
                    s.confirmedMs = Math.max(s.confirmedMs ?? 0, ms(next.updatedAt));
                } catch (e) {
                    console.error('[dashboard] Layout nicht gespeichert', e);
                    // Vorgemerkt bleibt der juengste Stand: kam waehrenddessen
                    // ein neuerer dazu, gilt der, sonst der gescheiterte.
                    if (!s.queued) s.queued = next;
                    setStatus(s, 'error');
                    return;
                }
            }
            setStatus(s, 'saved');
        } finally {
            s.inflight = false;
            settle(userId);
        }
    };

    return {
        request: (userId, layout) => {
            const s = stateOf(userId);
            if (s.queued && ms(s.queued.updatedAt) > ms(layout.updatedAt)) return;
            s.queued = layout;
            setStatus(s, 'saving');
            void pump(userId);
        },
        retry: (userId, fallback) => {
            const s = stateOf(userId);
            if (!s.queued && fallback) s.queued = fallback;
            if (!s.queued) return;
            setStatus(s, 'saving');
            void pump(userId);
        },
        status: (userId) => stateOf(userId).status,
        subscribe: (userId, fn) => {
            const s = stateOf(userId);
            s.listeners.add(fn);
            return () => { s.listeners.delete(fn); };
        },
        needsPush: (userId, layout, serverUpdatedAt) => {
            const own = ms(layout.updatedAt);
            if (own <= 0) return false;
            const s = stateOf(userId);
            if (s.queued && ms(s.queued.updatedAt) >= own) return false;
            return own > Math.max(ms(serverUpdatedAt), s.confirmedMs ?? 0);
        },
        idle: (userId) => {
            const s = stateOf(userId);
            if (!s.inflight) return Promise.resolve();
            return new Promise<void>(resolve => {
                const list = waiters.get(userId) ?? [];
                list.push(resolve);
                waiters.set(userId, list);
            });
        },
    };
};
